import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { eventsApi, Event } from '../api/events';
import { applicationsApi } from '../api/applications';
import { useAuthStore } from '../store/authStore';

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, login } = useAuthStore();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (id) {
      eventsApi.getById(id).then((data) => {
        setEvent(data);
        setLoading(false);
      });
    }
  }, [id]);

  const handleApply = async () => {
    if (!isAuthenticated) {
      login();
      return;
    }

    try {
      await applicationsApi.create({
        eventId: id!,
        message,
      });
      setSuccess('Заявка успешно подана!');
      setOpenDialog(false);
      setMessage('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка при подаче заявки');
    }
  };

  if (loading || !event) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      {event.image && (
        <Box
          component="img"
          src={event.image}
          alt={event.title}
          sx={{ width: '100%', height: 400, objectFit: 'cover', borderRadius: 2, mb: 4 }}
        />
      )}

      <Chip label={event.status} color="primary" sx={{ mb: 2 }} />

      <Typography variant="h2" gutterBottom>
        {event.title}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonthIcon color="action" />
          <Typography variant="body1" color="text.secondary">
            {new Date(event.startDate).toLocaleDateString('ru-RU', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
            {event.endDate &&
              ` - ${new Date(event.endDate).toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}`}
          </Typography>
        </Box>

        {event.location && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOnIcon color="action" />
            <Typography variant="body1" color="text.secondary">
              {event.location}
            </Typography>
          </Box>
        )}
      </Box>

      <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap', mb: 4 }}>
        {event.description}
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="contained" size="large" onClick={() => setOpenDialog(true)}>
          Подать заявку
        </Button>
        {event.registrationLink && (
          <Button variant="outlined" size="large" href={event.registrationLink} target="_blank">
            Дополнительная регистрация
          </Button>
        )}
      </Box>

      {/* Application Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Подать заявку на {event.title}</DialogTitle>
        <DialogContent>
          <TextField
            label="Сопроводительное сообщение (необязательно)"
            multiline
            rows={4}
            fullWidth
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Отмена</Button>
          <Button onClick={handleApply} variant="contained">
            Подать заявку
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
